import { useState } from 'react';
import { useGetUsers } from '@practium/features/api';
import type { UserDTO } from '@practium/features/users';
import type { LogAuthorFilterUser } from '../../../types/log.types';
import styles from './UserAuthorPanel.module.css';

interface UserAuthorPanelProps {
	selectedAuthorId: string;
	selectedAuthor: LogAuthorFilterUser | null;
	onSelect: (author: LogAuthorFilterUser) => void;
	onClear: () => void;
	onClose: () => void;
}

const getUserName = (user: UserDTO) => [user.name, user.surName].filter(Boolean).join(' ');

const getUserInitials = (name: string, email: string) => {
	const source = name || email;
	const parts = source.split(/\s+/).filter(Boolean);
	if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
	return source.slice(0, 2).toUpperCase();
};

const getUserAvatarSrc = (profileImage?: string | null) => {
	if (!profileImage) return undefined;
	if (profileImage.startsWith('http') || profileImage.startsWith('/')) return profileImage;
	return `/uploads/${profileImage}`;
};

const mapUserToAuthor = (user: UserDTO): LogAuthorFilterUser => {
	const userName = getUserName(user);

	return {
		id: user._id,
		name: userName || user.email,
		email: user.email,
		profileImage: user.profileImage,
	};
};

export const UserAuthorPanel = ({
	selectedAuthorId,
	selectedAuthor,
	onSelect,
	onClear,
	onClose,
}: UserAuthorPanelProps) => {
	const [query, setQuery] = useState('');
	const { data: users, isLoading } = useGetUsers({
		page: 1,
		limit: 8,
		userNameOrEmail: query,
		enabled: query.trim().length >= 2,
	});

	return (
		<>
			<div className={styles.header}>
				<span className={styles.title}>Usuário</span>
			</div>
			<input
				className={styles.search}
				value={query}
				onChange={(event) => setQuery(event.target.value)}
				placeholder="Nome, sobrenome ou e-mail"
			/>
			{selectedAuthor && (
				<div className={styles.selectedAuthor}>
					<span className={styles.avatarFallback}>
						{getUserInitials(selectedAuthor.name, selectedAuthor.email)}
					</span>
					<span className={styles.text}>
						<span className={styles.name}>{selectedAuthor.name || selectedAuthor.email}</span>
						<span className={styles.email}>{selectedAuthor.email}</span>
					</span>
				</div>
			)}
			<div className={styles.results}>
				{query.trim().length < 2 && (
					<span className={styles.hint}>Digite ao menos 2 caracteres.</span>
				)}
				{query.trim().length >= 2 && isLoading && (
					<span className={styles.hint}>Buscando usuários...</span>
				)}
				{query.trim().length >= 2 && !isLoading && users.length === 0 && (
					<span className={styles.hint}>Nenhum usuário encontrado.</span>
				)}
				{query.trim().length >= 2 && users.map((user) => {
					const userName = getUserName(user);
					const avatarSrc = getUserAvatarSrc(user.profileImage);
					const initials = getUserInitials(userName, user.email);

					return (
						<button
							type="button"
							key={user._id}
							className={`${styles.option} ${
								selectedAuthorId === user._id ? styles.optionSelected : ''
							}`}
							onClick={() => {
								onSelect(mapUserToAuthor(user));
								onClose();
							}}
						>
							{avatarSrc ? (
								<img src={avatarSrc} alt={userName} className={styles.avatar} />
							) : (
								<span className={styles.avatarFallback}>{initials}</span>
							)}
							<span className={styles.text}>
								<span className={styles.name}>{userName || user.email}</span>
								<span className={styles.email}>{user.email}</span>
							</span>
						</button>
					);
				})}
			</div>
			{selectedAuthorId && (
				<button type="button" className={styles.clearBtn} onClick={onClear}>
					Limpar usuário
				</button>
			)}
		</>
	);
};
